import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useGeneratePtoPprRdDraftMutation,
  useGetPtoAosrRequisitesQuery,
  useListPtoEquipmentQuery,
  useListPtoPprRdSourcesQuery,
  type PtoPprDocKind,
  type PtoPprGenerateBody,
  type PtoPprRdSource,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  Actions,
  Banner,
  DataTable,
  Dialog,
  Field,
  Muted,
  SmallInput,
  TableWrap,
  errorText,
} from '../kfo/ui';
import { EquipmentImportDialog } from './EquipmentImportDialog';
import { EquipmentTable } from './EquipmentTable';
import {
  DOC_KINDS,
  MAX_REVISIONS,
  appendEqRows,
  docKindOf,
  docShort,
  emptyEqRow,
  eqRowToInput,
  isBlankEqRow,
  plural,
  validateEqRows,
  type EqRowDraft,
} from './model';
import {
  CheckLabel,
  DocKindSelect,
  EditArea,
  ErrorList,
  FormGrid,
  Hint,
  WideSelect,
  WizardFooter,
  WizardStep,
  WizardSteps,
} from './ui';

interface WizardForm {
  /** Вид документа (`DOC_KINDS`): ППР, ТК, ППР на высоте, ППР ПС. */
  kind: PtoPprDocKind;
  title: string;
  work_type: string;
  object_name: string;
  customer: string;
  location: string;
  start_date: string;
  end_date: string;
  shifts: string;
  /** Группа грунтов по трудности погружения (ГЭСН 05): '' — не задана. */
  soil_group: '' | '1' | '2';
  revision_ids: string[];
  equipment: EqRowDraft[];
  save_equipment: boolean;
  conditions: string;
}

const initialForm = (): WizardForm => ({
  kind: 'ppr',
  title: '',
  work_type: '',
  object_name: '',
  customer: '',
  location: '',
  start_date: '',
  end_date: '',
  shifts: '',
  soil_group: '',
  revision_ids: [],
  equipment: [emptyEqRow()],
  save_equipment: true,
  conditions: '',
});

const STEPS = ['Основное', 'РД', 'Техника', 'Особые условия'];

/** Подсказки в полях «Название» и «Вид работ» — по виду документа. */
const PLACEHOLDERS: Record<PtoPprDocKind, { title: string; workType: string }> = {
  ppr: {
    title: 'ППР на погружение железобетонных свай',
    workType: 'погружение железобетонных свай',
  },
  tk: {
    title: 'ТК на погружение железобетонных свай',
    workType: 'погружение железобетонных свай',
  },
  ppr_height: {
    title: 'ППР на высоте при монтаже металлоконструкций каркаса',
    workType: 'монтаж металлоконструкций, работы на кровле и с автовышки',
  },
  ppr_lifting: {
    title: 'ППР подъёмными сооружениями при монтаже каркаса',
    workType: 'монтаж и погрузочно-разгрузочные работы автокранами и кранами-манипуляторами',
  },
};

/** Почему ревизию нельзя взять в ППР: без текста модели опереться не на что. */
function unavailableReason(s: PtoPprRdSource): string | null {
  if (s.ingest_status !== 'indexed') {
    if (s.ingest_status === 'failed') return 'ошибка индексации';
    if (s.ingest_status === 'parsing') return 'индексируется…';
    return 'ещё не проиндексирована';
  }
  if (s.chunks <= 0) return 'нет текста (0 фрагментов)';
  return null;
}

/** Ревизии к отправке: отмеченные и всё ещё доступные (индексация могла сброситься). */
const selectedRevisions = (f: WizardForm, availableIds: Set<string>) =>
  f.revision_ids.filter((id) => availableIds.has(id));

function stepErrors(step: number, f: WizardForm, availableIds: Set<string>): string[] {
  switch (step) {
    case 0: {
      const errs: string[] = [];
      if (!f.title.trim()) errs.push(`Укажите название ${docShort(f.kind)}.`);
      if (!f.work_type.trim()) errs.push('Укажите вид работ.');
      if (f.start_date && f.end_date && f.end_date < f.start_date) {
        errs.push('Окончание работ раньше начала.');
      }
      return errs;
    }
    case 1: {
      const n = selectedRevisions(f, availableIds).length;
      if (n === 0) return ['Выберите хотя бы одну проиндексированную ревизию РД.'];
      if (n > MAX_REVISIONS) {
        return [`Не больше ${MAX_REVISIONS} ревизий РД в одном документе.`];
      }
      return [];
    }
    case 2:
      return validateEqRows(f.equipment);
    default:
      return [];
  }
}

const opt = (s: string): string | undefined => s.trim() || undefined;

/** Пока открыто окно загрузки техники, Escape закрывает только его, не мастер. */
const keepOpen = () => {};

/** Тело запуска генерации (проверки всех шагов — до вызова). */
function toBody(f: WizardForm, availableIds: Set<string>): PtoPprGenerateBody {
  return {
    kind: f.kind,
    title: f.title.trim(),
    work_type: f.work_type.trim(),
    object_name: opt(f.object_name),
    customer: opt(f.customer),
    location: opt(f.location),
    revision_ids: selectedRevisions(f, availableIds),
    equipment: f.equipment.filter((r) => !isBlankEqRow(r)).map(eqRowToInput),
    conditions: opt(f.conditions),
    start_date: f.start_date || undefined,
    end_date: f.end_date || undefined,
    shifts: opt(f.shifts),
    soil_group: f.soil_group ? Number(f.soil_group) : undefined,
    save_equipment: f.save_equipment,
  };
}

/**
 * Мастер «Новый ППР по РД (ИИ)» (docs/pto-ppr-rd-spec.md §9): основное → РД →
 * техника → особые условия. Запуск ставит генерацию в очередь; документ
 * появится в списке черновиков после утверждения агентом-аудитором.
 */
export function PprRdWizard({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: string;
  onClose: () => void;
  onCreated: (draftId: string) => void;
}) {
  const [form, setForm] = useState<WizardForm>(initialForm);
  const initial = useRef(form);
  const [step, setStep] = useState(0);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [prefilledReqs, setPrefilledReqs] = useState(false);

  const sourcesQ = useListPtoPprRdSourcesQuery(projectId, { refetchOnMountOrArgChange: true });
  const registryQ = useListPtoEquipmentQuery(undefined, { refetchOnMountOrArgChange: true });
  const reqsQ = useGetPtoAosrRequisitesQuery(projectId, { skip: !projectId });
  const [generate, { isLoading: submitting }] = useGeneratePtoPprRdDraftMutation();

  // Автоподстановка реквизитов объекта из единого паспорта проекта (АОСР/ОЖР/Договор)
  useEffect(() => {
    if (prefilledReqs || !reqsQ.data) return;
    const r = reqsQ.data.requisites;
    const fb = reqsQ.data.fallback;
    const obj = r?.object_name || fb?.object_name || '';
    const cust = r?.developer || fb?.developer || '';
    const loc = r?.object_address || fb?.object_address || '';
    if (obj || cust || loc) {
      setForm((prev) => {
        const nextForm = {
          ...prev,
          object_name: prev.object_name.trim() || obj,
          customer: prev.customer.trim() || cust,
          location: prev.location.trim() || loc,
        };
        initial.current = nextForm;
        return nextForm;
      });
    }
    setPrefilledReqs(true);
  }, [prefilledReqs, reqsQ.data]);

  const sources = useMemo(() => sourcesQ.data?.items ?? [], [sourcesQ.data]);
  const registry = useMemo(() => registryQ.data?.items ?? [], [registryQ.data]);
  const available = useMemo(() => sources.filter((s) => !unavailableReason(s)), [sources]);
  const availableIds = useMemo(() => new Set(available.map((s) => s.revision_id)), [available]);

  const set = <K extends keyof WizardForm>(key: K, value: WizardForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const errors = stepErrors(step, form, availableIds);
  const filledEquipment = form.equipment.filter((r) => !isBlankEqRow(r)).length;
  const aiEquipment = form.equipment.filter(
    (r) => r.ai_fields.length > 0 && !isBlankEqRow(r),
  ).length;

  const requestClose = useCallback(() => {
    if (submitting) return;
    if (
      form !== initial.current &&
      !window.confirm('Закрыть мастер? Введённые данные не сохранятся.')
    ) {
      return;
    }
    onClose();
  }, [form, submitting, onClose]);

  function next() {
    if (errors.length > 0) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function goTo(target: number) {
    if (target <= step) {
      setShowErrors(false);
      setStep(target);
      return;
    }
    // Вперёд — только через проверенные шаги.
    for (let s = step; s < target; s++) {
      if (stepErrors(s, form, availableIds).length > 0) {
        setStep(s);
        setShowErrors(true);
        return;
      }
    }
    setShowErrors(false);
    setStep(target);
  }

  async function submit() {
    for (let s = 0; s < STEPS.length; s++) {
      if (stepErrors(s, form, availableIds).length > 0) {
        setStep(s);
        setShowErrors(true);
        return;
      }
    }
    setSubmitError('');
    try {
      const res = await generate({ projectId, ...toBody(form, availableIds) }).unwrap();
      onCreated(res.draft.id);
    } catch (e) {
      setSubmitError(errorText(e, `Не удалось запустить генерацию ${docShort(form.kind)}.`));
    }
  }

  function toggleRevision(id: string) {
    set(
      'revision_ids',
      form.revision_ids.includes(id)
        ? form.revision_ids.filter((x) => x !== id)
        : [...form.revision_ids, id],
    );
  }

  return (
    <Dialog
      open
      title={`Новый ${docShort(form.kind)} по РД (ИИ)`}
      onClose={importOpen ? keepOpen : requestClose}
      width={1440}
    >
      <WizardSteps>
        {STEPS.map((label, i) => (
          <WizardStep
            key={label}
            type="button"
            $active={i === step}
            $done={i < step}
            disabled={submitting}
            onClick={() => goTo(i)}
          >
            {i + 1}. {label}
          </WizardStep>
        ))}
      </WizardSteps>

      {step === 0 && (
        <FormGrid data-wizard-step="main">
          <Field className="wide">
            Тип документа
            <DocKindSelect
              value={form.kind}
              onChange={(e) => set('kind', docKindOf(e.target.value))}
            >
              {DOC_KINDS.map(([code, , full]) => (
                <option key={code} value={code}>
                  {full}
                </option>
              ))}
            </DocKindSelect>
          </Field>
          <Field>
            Название *
            <SmallInput
              value={form.title}
              placeholder={PLACEHOLDERS[form.kind].title}
              onChange={(e) => set('title', e.target.value)}
            />
          </Field>
          <Field>
            Вид работ *
            <SmallInput
              value={form.work_type}
              placeholder={PLACEHOLDERS[form.kind].workType}
              onChange={(e) => set('work_type', e.target.value)}
            />
          </Field>
          <Field>
            Объект
            <SmallInput
              value={form.object_name}
              onChange={(e) => set('object_name', e.target.value)}
            />
            <Hint>если пусто — возьмём из РД</Hint>
          </Field>
          <Field>
            Заказчик
            <SmallInput value={form.customer} onChange={(e) => set('customer', e.target.value)} />
          </Field>
          <Field>
            Местоположение
            <SmallInput
              value={form.location}
              placeholder="г. Тобольск, промплощадка"
              onChange={(e) => set('location', e.target.value)}
            />
          </Field>
          <Field>
            Сменность
            <SmallInput
              value={form.shifts}
              placeholder="2 смены по 11 ч"
              onChange={(e) => set('shifts', e.target.value)}
            />
          </Field>
          <Field>
            Группа грунтов по трудности погружения свай
            <WideSelect
              value={form.soil_group}
              onChange={(e) =>
                set(
                  'soil_group',
                  e.target.value === '1' || e.target.value === '2' ? e.target.value : '',
                )
              }
            >
              <option value="">не задана — принять 2 (в запас)</option>
              <option value="1">группа 1</option>
              <option value="2">группа 2</option>
            </WideSelect>
            <Hint>по ГЭСН 05 и изысканиям — для калькуляции и графика свайных работ</Hint>
          </Field>
          <Field>
            Начало работ
            <SmallInput
              type="date"
              value={form.start_date}
              onChange={(e) => set('start_date', e.target.value)}
            />
          </Field>
          <Field>
            Окончание работ
            <SmallInput
              type="date"
              value={form.end_date}
              onChange={(e) => set('end_date', e.target.value)}
            />
          </Field>
        </FormGrid>
      )}

      {step === 1 && (
        <div data-wizard-step="rd">
          <Muted>
            Отметьте ревизии РД, по которым составляется {docShort(form.kind)}. Доступны только
            проиндексированные — модель опирается на их текст и позиции объёма.
          </Muted>
          {sourcesQ.isLoading ? (
            <Muted>Загрузка РД проекта…</Muted>
          ) : sourcesQ.error ? (
            <Banner $tone="danger">
              {errorText(sourcesQ.error, 'Не удалось загрузить РД проекта.')}
            </Banner>
          ) : sources.length === 0 ? (
            <Banner $tone="warning">
              В проекте нет загруженной РД. Загрузите документы на странице проекта и дождитесь
              индексации.
            </Banner>
          ) : (
            <>
              <Actions>
                <Button
                  $variant="ghost"
                  $size="sm"
                  type="button"
                  disabled={available.length === 0}
                  onClick={() =>
                    set(
                      'revision_ids',
                      available.slice(0, MAX_REVISIONS).map((s) => s.revision_id),
                    )
                  }
                >
                  Выбрать все доступные
                </Button>
                <Button
                  $variant="ghost"
                  $size="sm"
                  type="button"
                  disabled={form.revision_ids.length === 0}
                  onClick={() => set('revision_ids', [])}
                >
                  Снять выбор
                </Button>
                <Muted as="span">
                  Выбрано: {form.revision_ids.length} из {available.length} доступных
                </Muted>
              </Actions>
              <TableWrap $maxHeight="52vh">
                <DataTable>
                  <thead>
                    <tr>
                      <th />
                      <th>Шифр</th>
                      <th>Наименование</th>
                      <th>Ревизия</th>
                      <th className="num">Страниц</th>
                      <th className="num">Фрагментов</th>
                      <th className="num">Позиций объёма</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((s) => {
                      const reason = unavailableReason(s);
                      const checked = form.revision_ids.includes(s.revision_id);
                      return (
                        <tr
                          key={s.revision_id}
                          data-click={reason ? undefined : 'true'}
                          data-tone={reason ? 'muted' : undefined}
                          data-revision={s.revision_id}
                          onClick={() => !reason && toggleRevision(s.revision_id)}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!!reason}
                              aria-label={`Выбрать ${s.document_code}`}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleRevision(s.revision_id)}
                            />
                          </td>
                          <td className="nowrap">
                            {s.document_code}
                            {s.mark ? <Muted as="div">{s.mark}</Muted> : null}
                          </td>
                          <td className="wrap">{s.document_title}</td>
                          <td className="nowrap">
                            рев. {s.revision_no}
                            {s.rev_label ? ` (${s.rev_label})` : ''}
                          </td>
                          <td className="num">{s.total_pages ?? '—'}</td>
                          <td className="num">{s.chunks}</td>
                          <td className="num">{s.positions}</td>
                          <td>
                            {reason ? (
                              <Muted as="span">{reason}</Muted>
                            ) : s.positions === 0 ? (
                              <Hint>нет позиций объёма — таблица объёмов будет пустой</Hint>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </DataTable>
              </TableWrap>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div data-wizard-step="equipment">
          <Muted>
            График мобилизации техники (форма ППР-004). Начните вводить наименование — техника из
            справочника подставится вместе с характеристиками и ГВХ. Для кранов укажите г/п, вылет и
            ГВХ: по ним код проверит грузоподъёмность против масс элементов из РД. Перечень техники
            можно загрузить файлом Excel или CSV — недостающие характеристики допишет ИИ.
          </Muted>
          <EquipmentTable
            rows={form.equipment}
            registry={registry}
            onChange={(rows) => set('equipment', rows)}
            onImport={() => setImportOpen(true)}
          />
          {aiEquipment > 0 && (
            <Banner $tone="warning">
              В {aiEquipment} {plural(aiEquipment, ['строке', 'строках', 'строках'])} техники есть
              значения с пометкой «ИИ» — справочные данные по марке техники. Сверьте их с паспортами
              машин: «ИИ ×» или правка значения снимает пометку, а в {docShort(form.kind)}{' '}
              оставшиеся пойдут как «справочные данные ИИ, сверить с паспортом».
            </Banner>
          )}
          <CheckLabel>
            <input
              type="checkbox"
              checked={form.save_equipment}
              onChange={(e) => set('save_equipment', e.target.checked)}
            />
            Запомнить новую технику в справочнике
          </CheckLabel>
          {filledEquipment === 0 && (
            <Banner $tone="warning">
              Техника не указана — без неё аудитор не утвердит разделы о механизации и
              грузоподъёмных работах. Можно продолжить и дописать технику позже.
            </Banner>
          )}
        </div>
      )}

      {step === 3 && (
        <div data-wizard-step="conditions">
          <Field>
            Особые условия производства работ
            <EditArea
              $rows={7}
              value={form.conditions}
              placeholder={
                'Например:\n' +
                '— стеснённые условия, работы рядом с действующим производством;\n' +
                '— зимний период, прогрев бетона;\n' +
                '— существующие подземные коммуникации в зоне работ;\n' +
                '— требования заказчика (наряды-допуски, пропускной режим, окна работ).'
              }
              onChange={(e) => set('conditions', e.target.value)}
            />
          </Field>
          <Banner $tone="info">
            <b>{form.title.trim() || docShort(form.kind)}</b> —{' '}
            {form.work_type.trim() || 'вид работ не указан'}. РД: {form.revision_ids.length}{' '}
            {plural(form.revision_ids.length, ['ревизия', 'ревизии', 'ревизий'])}; техника:{' '}
            {filledEquipment} {plural(filledEquipment, ['строка', 'строки', 'строк'])}.
            <br />
            ИИ составит разделы по РД и технике, затем агент-аудитор проверит документ и добьётся
            исправлений. Документ откроется после утверждения аудитором — обычно это занимает
            несколько минут.
          </Banner>
        </div>
      )}

      {showErrors && errors.length > 0 && (
        <Banner $tone="danger">
          <ErrorList>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ErrorList>
        </Banner>
      )}
      {submitError && <Banner $tone="danger">{submitError}</Banner>}

      <WizardFooter>
        <Button $variant="ghost" type="button" disabled={submitting} onClick={requestClose}>
          Отмена
        </Button>
        <Actions>
          {step > 0 && (
            <Button
              $variant="secondary"
              type="button"
              disabled={submitting}
              onClick={() => goTo(step - 1)}
            >
              ← Назад
            </Button>
          )}
          {step < STEPS.length - 1 ? (
            <Button $variant="primary" type="button" onClick={next}>
              Далее →
            </Button>
          ) : (
            <Button $variant="primary" type="button" disabled={submitting} onClick={submit}>
              {submitting ? 'Запуск…' : `Сформировать ${docShort(form.kind)}`}
            </Button>
          )}
        </Actions>
      </WizardFooter>

      {/* Окно загрузки — в body: поверх мастера, вне его прокрутки. */}
      {importOpen &&
        createPortal(
          <EquipmentImportDialog
            open
            mode="wizard"
            registry={registry}
            existingCount={filledEquipment}
            onClose={() => setImportOpen(false)}
            onAdd={(rows) => setForm((f) => ({ ...f, equipment: appendEqRows(f.equipment, rows) }))}
          />,
          document.body,
        )}
    </Dialog>
  );
}
